'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import {
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { limitNumberOfPaginationPages } from '@//utils/pagination';

const transformQuery = (q?: string) => {
  const p = new URLSearchParams(q || '');
  const params = new URLSearchParams();

  const offset = p.get('offset');
  if (offset && offset !== '0') params.set('offset', offset);

  const lat = p.get('lat');
  const lon = p.get('lon');
  if (lat && lon) {
    params.set('lat', lat);
    params.set('lon', lon);
  }

  const sort = p.get('sort');
  if (sort && sort !== 'publishDesc') params.set('sort', sort);

  params.sort();

  return params.toString() ? `?${params}` : '';
};

interface PaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const Pagination = ({ rootUrl, pagination }: PaginationProps) => {
  const t = useTranslations('other.pagination');
  const prev = pagination?.previousPage?.query
    ? `${rootUrl}${transformQuery(pagination.previousPage.query)}`
    : undefined;

  const next = pagination?.nextPage?.query
    ? `${rootUrl}${transformQuery(pagination.nextPage.query)}`
    : undefined;

  const pages = useMemo(
    () => limitNumberOfPaginationPages(pagination?.pages ?? []),
    [pagination?.pages],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('offset')) {
      document.getElementById('retailers-list')?.focus();
    }
  }, []);

  if (!pagination?.pages.length) return null;

  return (
    <StyledPagination testId="retailers-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink variant="prev" href={prev} testId="retailers-pagination-prev" />
      {pages.map((page) => (
        <PaginationLink
          key={page.displayName}
          href={`${rootUrl}${transformQuery(page.query)}`}
          title={t('goTo.title', { page: page.displayName })}
          testId={`retailers-pagination-page-${page.displayName}`}
          isActive={page.selected}
        >
          {page.displayName}
        </PaginationLink>
      ))}
      <PaginationNavLink variant="next" href={next} testId="retailers-pagination-next" />
    </StyledPagination>
  );
};

export default Pagination;
